import type { CSSProperties, FC, AnimationEventHandler } from 'react'
import cx from 'classnames'
import type { Photo, Video } from '../../types';
import { SIZES_FULL, SIZES_HALF } from '../../utils/responsiveImage';
import { markImageLoaded, markImageError } from '../../utils/markImageSettled';
import { ResponsiveImage } from '../ResponsiveImage';
import { UnstyledButton } from '../UnstyledButton';
import { useI18n } from '../../i18n';

import styles from './PhotoWidget.module.css'

type Props = {
  items: Array<Photo | Video>;
  onClick: (gallery: Array<Photo | Video>) => void;
  className?: string;
  style?: CSSProperties;
  onAnimationStart?: AnimationEventHandler<HTMLButtonElement>;
  onAnimationEnd?: AnimationEventHandler<HTMLButtonElement>;
};

export const PhotoWidget: FC<Props> = ({
  items,
  onClick,
  className,
  style,
  onAnimationStart,
  onAnimationEnd,
}) => {
  const i18n = useI18n();
  const modifier =
    items.length === 1
      ? styles.one
      : items.length === 2
      ? styles.two
      : styles.many;

  // Для единичного портретного фото — контейнер обрезает до квадрата
  const singleItem = items.length === 1 ? items[0] : null;
  const isPortraitPhoto =
    singleItem?.type === 'photo' &&
    singleItem.height > singleItem.width;

  const figureStyle: CSSProperties = isPortraitPhoto
    ? { ...style, '--aspectRatio': 1 } as CSSProperties
    : { ...style };

  const sizes = items.length === 1 ? SIZES_FULL : SIZES_HALF;
  const visibleItems = items.slice(0, 3);

  return (
    <figure
      className={cx(styles.figure, modifier, className)}
      style={figureStyle}
      onAnimationStart={onAnimationStart}
      onAnimationEnd={onAnimationEnd}
    >
      <UnstyledButton
        className={styles.root}
        onClick={() => onClick(items)}
        onPointerDown={(e) => e.preventDefault()}
        aria-label={i18n.chatInput.openMediaGallery}
      >
        <div className={styles.wrapper}>
          {visibleItems.map((item, i) => {
            const isLastVisible = i === visibleItems.length - 1;
            return (
              <div className={styles.cell} key={item.id}>
                <div className={styles.imgWrap}>
                  {item.type === 'photo' ? (
                    <ResponsiveImage
                      className={styles.img}
                      src={item.src}
                      sizes={sizes}
                      originalWidth={item.width}
                      width={item.width}
                      height={item.height}
                      alt={item.caption}
                    />
                  ) : (
                    <img
                      className={styles.img}
                      src={item.thumbnailSrc ?? item.src}
                      alt={item.caption}
                      onLoad={markImageLoaded}
                      onError={markImageError}
                    />
                  )}
                  {isLastVisible && items.length > 3 && (
                    <div className={styles.counter}>+{items.length - 3}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </UnstyledButton>
    </figure>
  );
};
